import { z } from 'zod';
import { AttachmentKind } from './content.js';
import { CursorQuery, Paginated } from './common/pagination.js';
/**
 * Uploaded media (FR-POST-02, FR-REEL-01). One bucket, two purposes: profile
 * and post imagery, and the short vertical videos behind Reels.
 *
 * The limits below are exported rather than inlined so the apps enforce the
 * same numbers the server does. A client that checks before spending a user's
 * mobile data on a 90MB upload the server will reject is the whole point —
 * but the server never trusts that check, and every rule here is re-applied
 * against the received bytes in `storage.policy.ts`.
 */
/**
 * Image formats accepted on upload.
 *
 * `image/svg+xml` is deliberately absent and must stay absent. An SVG is a
 * script container, not a picture: served from the public read bucket it
 * executes in whatever origin renders it, so accepting one turns an avatar
 * field into stored XSS. GIF is out for a different reason — an animated GIF
 * is a video with none of the size discipline of one, and Reels is the
 * supported path for motion.
 */
export const ImageMimeType = z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
/**
 * Video formats accepted on upload. MP4 (H.264/AAC) is what every phone
 * records and every browser plays; QuickTime is what iOS hands over when the
 * user picks from their camera roll; WebM covers desktop web capture.
 */
export const VideoMimeType = z.enum(['video/mp4', 'video/quicktime', 'video/webm']);
/** 10 MB — comfortably above a phone photo, far below a video. */
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** 100 MB — roughly 90 seconds of phone-recorded 1080p at a sane bitrate. */
export const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
/** Reels are short by definition (FR-REEL-01). */
export const REEL_MAX_DURATION_SECONDS = 90;
/**
 * Where an object may live. The prefix is part of the authorisation model, not
 * decoration: `parseObjectKey` reads the owner out of the second segment, so a
 * member can only delete what their own id prefixes.
 *
 * `{kind}/{ownerId}/{uuid}.{ext}` — no user-supplied text anywhere in it. The
 * original filename is never used to build a key: it is attacker-controlled,
 * arrives with whatever encoding the client felt like, and buys nothing once
 * the content type is recorded on the object.
 */
export const StorageObjectKey = z
    .string()
    .min(1)
    .max(300)
    .regex(/^(images|videos)\/[A-Za-z0-9_-]{1,64}\/(?:[a-z0-9][a-z0-9_-]{0,30}\/){0,3}[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[a-z0-9]{2,5}$/, 'must be a key this API issued');
/**
 * A caller-named folder inside the member's own space, e.g. `avatars`,
 * `posts/2026`, `reels`. Free text — any name the app wants — but VALIDATED,
 * not sanitised, and the distinction is the whole security argument.
 *
 * Sanitising strips what it recognises as dangerous and passes the rest, so it
 * is a denylist and the next encoding trick walks through it. This instead
 * states the only shape a folder may take and rejects everything else with a
 * 400: up to four segments, each starting alphanumeric and continuing in
 * lowercase letters, digits, `-` or `_`.
 *
 * What that makes structurally impossible rather than merely filtered: `..`
 * and `.` (a segment must start with a letter or digit and the set excludes
 * `.`), absolute paths and empty segments (no leading, trailing or doubled
 * `/`), NUL and control bytes, RTL overrides and every other Unicode trick,
 * and `%2e%2e` style encodings — the decoded string is what is matched.
 *
 * Lowercase only, because S3 keys are case-sensitive: allowing `Avatars` and
 * `avatars` would silently create two folders that look identical in a console
 * and in a bug report.
 */
export const StorageFolder = z
    .string()
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]{0,30}(?:\/[a-z0-9][a-z0-9_-]{0,30}){0,3}$/, 'up to four lowercase segments of letters, digits, - or _, separated by /');
/**
 * What an upload returns, and what `GET /storage/files` reads back.
 *
 * `url` is permanent: the bucket is public-read behind the CDN, so the URL
 * needs no signing and can be stored on a post or a profile the way
 * `avatarUrl` already is. Nothing sensitive is uploaded through this module —
 * if that changes, the bucket policy changes with it, not this schema.
 */
export const StoredFileView = z.object({
    key: StorageObjectKey,
    url: z.string().url(),
    kind: AttachmentKind,
    /** Absent when the file was uploaded straight into the member's root. */
    folder: StorageFolder.optional(),
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
    /** Videos only, and only ever what the client declared — see `UploadVideoFields`. */
    durationSeconds: z.number().int().positive().optional(),
    uploadedAt: z.coerce.date(),
});
export const DeletedFileView = z.object({
    key: StorageObjectKey,
    deleted: z.literal(true),
});
/** Addresses one object for read-back or deletion. */
export const StorageKeyQuery = z.object({
    key: StorageObjectKey,
});
/**
 * The text fields that ride alongside the file part on a video upload.
 *
 * `durationSeconds` is DECLARED BY THE CLIENT and bounded here — it is not
 * measured. Reading a real duration means parsing the container (ffprobe, or
 * MediaConvert on the object after the fact), which this module does not do,
 * so a hostile client can under-report and land a longer video than the cap.
 * The bound still earns its place: it rejects the honest 10-minute-file
 * mistake at the door instead of after 100MB has crossed the wire, and it
 * gives the Reels player a duration to render before the video loads. Treat it
 * as a declared attribute, never as an enforced one.
 */
export const UploadVideoFields = z.object({
    durationSeconds: z.coerce
        .number()
        .int()
        .positive()
        .max(REEL_MAX_DURATION_SECONDS, `a reel may run at most ${REEL_MAX_DURATION_SECONDS} seconds`),
    folder: StorageFolder.optional(),
});
/** Images carry no duration, so a folder is the only thing riding along. */
export const UploadImageFields = z.object({
    folder: StorageFolder.optional(),
});
/**
 * Browse the CALLER'S OWN uploads. Always scoped to their own prefix — this
 * endpoint cannot be pointed at another member, which is why it takes no
 * owner parameter.
 *
 * `kind` unset walks images first, then videos; the cursor carries which of
 * the two it stopped in, so paging straight through both is one loop for the
 * caller.
 */
export const StorageListQuery = CursorQuery.extend({
    kind: AttachmentKind.optional(),
    /** Narrow to one folder. Matches that folder and everything beneath it. */
    folder: StorageFolder.optional(),
});
/**
 * A page of stored files.
 *
 * `durationSeconds` is absent on every item here even for videos: S3's list
 * operation returns key, size and mtime but no user metadata, and fetching it
 * would mean one HEAD per row. Read a single file back through
 * `GET /storage/files` when the duration is actually needed.
 */
export const StoredFilePage = Paginated(StoredFileView);
//# sourceMappingURL=storage.js.map